import { ImageVariant } from '-/Custom/Util.types';
import { useEffect, useRef, useState } from 'react';
import { StyleSheet } from 'react-native-unistyles';
import { useChatDetailContext } from '@/components/Chat/ChatDetailContext';
import { ChatMessageItemFragment } from '@/components/Chat/ChatMessageItem.utils';
import Box from '@/components/UI/Box';
import Text from '@/components/UI/Text';
import Icon from '@/components/Unistyles/Icon';
import Pressable from '@/components/Unistyles/Pressable';
import { type FragmentOf, readFragment } from '@/gql/graphql';
import useHaptics from '@/hooks/useHaptics';
import useChatMediaCacheStore from '@/stores/useChatMediaCacheStore';
import { LONG_PRESS_DELAY } from '@/utils/constants';
import getLinkSegments from '@/utils/getLinkSegments';
import getPublicUrl from '@/utils/getPublicUrl';
import isDesktopUserAgent from '@/utils/isDesktopUserAgent';
import { resolveMessage } from '@/utils/messageMentions';
import ChatMediaCarousel from './ChatMediaCarousel';
import ChatMediaOgItem from './ChatMediaOgItem';
import ChatMediaRemoteItem from './ChatMediaRemoteItem';
import ChatMediaUploadedItem from './ChatMediaUploadedItem';
import ChatMessageContent from './ChatMessageContent';
import ChatMessageHeader from './ChatMessageHeader';
import ChatMessageItemMenu from './ChatMessageItemMenu';
import * as ChatMessageItemReaction from './ChatMessageItemReaction';

type ChatMessageItemProps = {
  fragment: FragmentOf<typeof ChatMessageItemFragment>;
  isGroup: boolean;
  isBeingEdited: boolean;
  index: number;
};
const isHoverDisabled = () =>
  process.env.EXPO_OS === 'web' &&
  typeof document !== 'undefined' &&
  document.body?.classList.contains('chat-hover-disabled');
function ChatMessageItem({
  fragment,
  isGroup,
  isBeingEdited,
  index,
}: ChatMessageItemProps) {
  const item = readFragment(ChatMessageItemFragment, fragment);
  const [hasMenu, setHasMenu] = useState(false);
  const detailSheetRef = useChatDetailContext((state) => state.detailSheetRef);
  const inputRef = useChatDetailContext((state) => state.inputRef);
  const setContext = useChatDetailContext((state) => state.setContext);
  const pointerInsideRef = useRef(false);
  const {
    id,
    content,
    content_template: contentTemplate,
    creator,
    created_at: createdAt,
    edited_at: editedAt,
    deleted_at: deletedAt,
    media,
    mentions,
    reaction_count,
  } = readFragment(ChatMessageItemFragment, item);
  const onLongPress = useHaptics(() => {
    inputRef?.current?.blur();
    setContext({
      selectedMessageId: id,
    });
    detailSheetRef?.current?.present();
  });
  const pendingMediaAssets = useChatMediaCacheStore((state) => state.assets);
  const resolvedMessage = resolveMessage(content, contentTemplate, mentions);
  const segments = getLinkSegments(resolvedMessage.plainText, getPublicUrl());
  const ogMedia = media.filter((m) => !!m.og_title);
  const remoteMedia = media.filter((m) => !!m.mime_type);
  const uploadedMedia = media.filter(
    (m) => !!m.media_id || !!pendingMediaAssets[m.id],
  );
  const isCollapsibleIntoMedia =
    ((remoteMedia.length === 1 && !ogMedia.length) ||
      (ogMedia.length === 1 && !remoteMedia.length)) &&
    segments.length === 1 &&
    segments[0].isLink;
  useEffect(() => {
    if (process.env.EXPO_OS !== 'web' || typeof window === 'undefined') {
      return;
    }
    const handleHoverEnabled = () => {
      if (!deletedAt && pointerInsideRef.current && !isHoverDisabled()) {
        setHasMenu(true);
      }
    };
    window.addEventListener('chat-hover-enabled', handleHoverEnabled);
    return () => {
      window.removeEventListener('chat-hover-enabled', handleHoverEnabled);
    };
  }, [deletedAt]);
  if (
    !(
      resolvedMessage.plainText ||
      (!deletedAt && uploadedMedia.length === 1) ||
      (!deletedAt && uploadedMedia.length > 1)
    )
  ) {
    return null;
  }
  const desktopProps = {
    onPointerEnter: () => {
      pointerInsideRef.current = true;
      if (!deletedAt && !isHoverDisabled()) {
        setHasMenu(true);
      }
    },
    onPointerLeave: () => {
      pointerInsideRef.current = false;
      if (!deletedAt) {
        setHasMenu(false);
      }
    },
  };
  return (
    <Pressable
      testID="ChatMessageItem"
      disabled={!!deletedAt}
      style={style.container({
        isGroup,
        isBeingEdited,
        index,
      })}
      onLongPress={onLongPress}
      delayLongPress={LONG_PRESS_DELAY}
      pointerEvents={deletedAt ? 'none' : 'auto'}
      {...(isDesktopUserAgent ? desktopProps : {})}
    >
      {!deletedAt && hasMenu ? <ChatMessageItemMenu fragment={item} /> : null}
      {isGroup ? (
        <ChatMessageHeader
          name={creator?.name || ''}
          createdAt={createdAt}
          avatarId={creator?.avatar_id || ''}
          avatarColor={creator?.color || ''}
        />
      ) : null}

      <Box style={style.contentContainer}>
        {deletedAt ? (
          <Text style={style.deletedMessage}>Message Deleted</Text>
        ) : null}

        {!deletedAt && resolvedMessage.plainText && !isCollapsibleIntoMedia ? (
          <ChatMessageContent
            editedAt={editedAt}
            messageSegments={resolvedMessage.segments}
          />
        ) : null}

        {!deletedAt && uploadedMedia.length === 1 ? (
          <Box style={style.singleImageContainer}>
            <ChatMediaUploadedItem
              id={uploadedMedia[0].id}
              mediaId={uploadedMedia[0].media_id}
              pendingAsset={pendingMediaAssets[uploadedMedia[0].id]}
              variant={ImageVariant.Large3x}
              createdBy={uploadedMedia[0].created_by}
            />
          </Box>
        ) : !deletedAt && uploadedMedia.length > 1 ? (
          <ChatMediaCarousel media={uploadedMedia} />
        ) : null}

        {!deletedAt &&
          remoteMedia.map((remoteMediaItem) => (
            <ChatMediaRemoteItem
              key={remoteMediaItem.id}
              url={remoteMediaItem.url}
            />
          ))}

        {!deletedAt &&
          ogMedia.map((ogMediaItem) => (
            <ChatMediaOgItem
              key={ogMediaItem.id}
              imageUrl={ogMediaItem.og_image_url}
              url={ogMediaItem.url}
              title={ogMediaItem.og_title}
              description={ogMediaItem.og_description}
            />
          ))}

        {!deletedAt && reaction_count.length ? (
          <ChatMessageItemReaction.default fragment={item}>
            <ChatMessageItemReaction.Item
              onPress={() => {
                inputRef?.current?.blur();
                setContext({
                  selectedMessageId: id,
                });
                detailSheetRef?.current?.present();
              }}
            >
              <Icon name="happy-outline" />
            </ChatMessageItemReaction.Item>
          </ChatMessageItemReaction.default>
        ) : null}
      </Box>
    </Pressable>
  );
}
const style = StyleSheet.create((theme) => ({
  container: ({
    isGroup,
    isBeingEdited,
    index,
  }: {
    isGroup: boolean;
    isBeingEdited: boolean;
    index: number;
  }) => ({
    flex: 1,
    zIndex: -index,
    marginTop: isGroup ? theme.space('xl') : 0,
    cursor: 'auto',
    ...(isBeingEdited
      ? {
          backgroundColor: theme.colors.primaryMuted,
        }
      : {}),
    _web: {
      _hover: {
        backgroundColor: theme.colors.backgroundMuted,
      },
    },
  }),
  contentContainer: {
    flex: 1,
    paddingHorizontal: theme.space(12),
    paddingTop: theme.space('xs'),
    gap: theme.space('sm'),
  },
  singleImageContainer: {
    marginTop: 4,
  },
  deletedMessage: {
    fontSize: theme.fontSize.md,
    color: theme.colors.typographyMuted,
    fontStyle: 'italic',
    fontWeight: theme.fontWeight.normal,
  },
}));
export default ChatMessageItem;
